import { Icon, ICONS } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { signIn, signInBySharedLogin } from '@creditsights/api-ui-web';
import { ROUTES } from '../../Base.constants';
import { useToast } from '../../hooks/toast/useToast';
import styles from './Login.module.scss';

const cx = classNames.bind(styles);

type LoginForm = {
  username: string;
  password: string;
};

const Login = (): React.ReactElement => {
  const [local, setLocal] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>();
  const { hideToast } = useToast();

  const clearGenieCookie = () => {
    const d = new Date();
    d.setTime(d.getTime() - 1);
    const expires = `expires=${d.toUTCString()}`;
    document.cookie = `cs-genie-session-data=;${expires};path=/`;
  };

  useEffect(() => {
    hideToast();

    // We are going to redirect to Fitch Login by default. If you need legacy login please use /login?local=true
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('cstoken')) {
      sharedLogin(searchParams.get('cstoken'));
    } else if (searchParams.get('local') !== 'true') {
      navigateToFitchConnect();
      clearGenieCookie();
      setLocal(false);
    } else {
      setLocal(true);
    }
  }, [window.location.search]);

  const getRedirectUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirect = urlParams.get('redirect');
    return redirect || ROUTES.feed;
  };

  const handleRedirect = () => {
    const { location } = window;

    if (location.pathname.startsWith(ROUTES.login)) {
      location.href = getRedirectUrl();
    }
  };

  const onSubmit = async ({ username, password }) => {
    try {
      setPending(true);
      setError(false);
      await signIn(username, password);
      clearGenieCookie();
      handleRedirect();
    } catch (e) {
      setError(true);
    } finally {
      setPending(false);
    }
  };

  const sharedLogin = async (token) => {
    await signInBySharedLogin(token);
    clearGenieCookie();
    handleRedirect();
  };

  const getCallbackUrlParams = (): string => {
    const { location } = window;

    return location.pathname.startsWith(ROUTES.login) ? getRedirectUrl() : location.pathname + location.search;
  };

  const navigateToFitchConnect = (e: any = null) => {
    if (e) {
      e.preventDefault();
    }
    const l = window.location;
    const callbackUrlParams = getCallbackUrlParams();
    const callback = `${l.protocol}//${l.hostname}/callback?url_params=${encodeURIComponent(callbackUrlParams)}`;
    l.href = `/api/authentication/v1/auth/connect?callbackUrl=${encodeURIComponent(callback)}`;
  };

  if (local) {
    return (
      <div className={cx('login-ctx')}>
        <div className={cx('login-form')}>
          <div className={cx('image-wrapper')}>
            <div className={cx('logo')}>
              <Icon name={ICONS.ColorLogo} width={220} height={45} />
            </div>
            <div className={cx('user')}>
              <i className="user circle icon" />
            </div>
          </div>
          <form className={cx('ui form')} onSubmit={handleSubmit(onSubmit)}>
            <div className={cx('field')}>
              <input type="text" placeholder="Username" {...register('username', { required: true })} />
              {errors.username && <div className={cx('error-message')}>Username is required</div>}
            </div>
            <div className={cx('field')}>
              <input type="password" placeholder="Password" {...register('password', { required: true })} />
              {errors.password && <div className={cx('error-message')}>Password is required</div>}
            </div>
            {error && <div className={cx('error-message')}>Unable to login. Please try again.</div>}
            <div className={cx('buttons-ctx')}>
              <button className={cx('fluid ui button')} type="submit" disabled={pending} data-testid="login-button">
                Sign In
              </button>
              <button
                className={cx('fluid ui button green')}
                type="button"
                disabled={pending}
                data-testid="fitch-auth-button"
                onClick={(e) => navigateToFitchConnect(e)}
              >
                Sign in with Fitch Auth
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return <span />;
};

export default Login;
